import { Writeable } from '@anytag/shared-utils';
import { FormAction } from '@anytag/shared-types';
import { objectKeys } from '@anytag/shared-assets';
import { useQueryHelper } from '@anytag/shared-hooks';
import { MarketplaceInfluencerRevenueInput, useUpdateMarketplaceInfluencerCostMutation } from '@anytag/graphql';

interface UpdatePostPricesProps {
  marketplaceId: number;
  refetchQueries: string[];
}
export const useUpdatePostPrices = ({ marketplaceId, refetchQueries }: UpdatePostPricesProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { callUpdateMarketplaceInfluencerCost } = useUpdateMarketplaceInfluencerCostMutation({
    refetchQueries
  });

  const handleUpdatePostPrices: FormAction<UpdateCostsFormValues>['onSubmit'] = async (
    values,
    { formState, reset }
  ) => {
    try {
      await callUpdateMarketplaceInfluencerCost({
        variables: {
          input: {
            marketplaceId,
            influencers: objectKeys(values).reduce<MarketplaceInfluencerRevenueInputShape[]>((acc, curr) => {
              if (formState.defaultValues?.[curr] !== values[curr]) {
                const [influencerId, costType] = curr.split('_') as [string, RevenueInputKeys];
                const value = values[curr];
                // Find the existing object for this influencerId and update it's cost or create a new one
                const existingEntry = acc.find((entry) => entry.influencerId === Number(influencerId));
                if (existingEntry) {
                  existingEntry[costType] =
                    value !== ''
                      ? costType === 'influencerCommissionRate'
                        ? Number(value) / 100
                        : Number(value)
                      : null;
                } else {
                  const newEntry: MarketplaceInfluencerRevenueInput = {
                    influencerId: Number(influencerId),
                    [costType]:
                      value !== ''
                        ? costType === 'influencerCommissionRate'
                          ? Number(value) / 100
                          : Number(value)
                        : null
                  };
                  acc.push(newEntry);
                }
              }

              return acc;
            }, [])
          }
        }
      });
      enqueueSnackbar(t('succeededInSave'), { variant: 'success' });
      reset(values);
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return { handleUpdatePostPrices };
};

export type RevenueInputKeys = keyof Omit<MarketplaceInfluencerRevenueInput, 'influencerId'>;
export type UpdateCostsFormValues = {
  [K in `${number}_${RevenueInputKeys}`]?: string;
};
export type MarketplaceInfluencerRevenueInputShape = Writeable<MarketplaceInfluencerRevenueInput>;
